/**
 * This or That on the home panel: the question, two option buttons, and after
 * a vote, each side's share. Results only arrive from the server once you have
 * voted, so there is nothing to peek at first. Changing your vote is allowed
 * until the poll closes. The pie icon opens the deep dive on every past poll
 * (/central/this-or-that/results, Sasha 10/02).
 */

import { Link } from 'react-router-dom';

import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { ArrowSwapRegular, DataPieRegular } from '@fluentui/react-icons';
import { borderRadius, semanticColors, veracityColors } from '@shared/ui';
import { pollQuestion, useVote, type Poll, type PollChoice } from './api';

const useStyles = makeStyles({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: borderRadius.editorial,
    border: `1px solid ${semanticColors.borderSubtle}`,
    padding: '10px 12px 12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  head: { display: 'flex', alignItems: 'center', gap: '8px' },
  disc: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    display: 'grid',
    placeItems: 'center',
    flexShrink: 0,
    fontSize: '16px',
    backgroundColor: veracityColors.firstLight,
    color: veracityColors.veracityOrange,
  },
  label: {
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: tokens.colorBrandForegroundLink,
  },
  category: {
    marginLeft: 'auto',
    fontSize: '11px',
    color: semanticColors.textSecondary,
  },
  pie: {
    display: 'grid',
    placeItems: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    fontSize: '18px',
    color: tokens.colorBrandForegroundLink,
    ':hover': { backgroundColor: veracityColors.firstLight },
    ':focus-visible': { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: '1px' },
  },
  pieAlone: { marginLeft: 'auto' },
  question: {
    fontSize: '13.5px',
    fontWeight: 600,
    color: veracityColors.midnightCharcoal,
    lineHeight: 1.35,
  },
  options: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' },
  option: {
    position: 'relative',
    overflow: 'hidden',
    padding: '8px 10px',
    borderRadius: borderRadius.circular,
    border: `1px solid ${semanticColors.borderDefault}`,
    backgroundColor: '#FFFFFF',
    color: veracityColors.midnightCharcoal,
    fontFamily: 'inherit',
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
    ':hover': { backgroundColor: veracityColors.firstLight },
    ':disabled': { cursor: 'progress' },
    ':focus-visible': { outline: `2px solid ${tokens.colorBrandStroke1}`, outlineOffset: '2px' },
  },
  chosen: {
    backgroundColor: tokens.colorBrandBackground,
    border: `1px solid ${tokens.colorBrandBackground}`,
    color: '#FFFFFF',
    ':hover': { backgroundColor: tokens.colorBrandBackgroundHover },
  },
  meta: { fontSize: '11.5px', color: semanticColors.textSecondary },
  error: { fontSize: '12px', color: tokens.colorPaletteRedForeground1 },
});

function pct(n: number, total: number): number {
  return total === 0 ? 0 : Math.round((n / total) * 100);
}

export function ThisOrThat({ poll }: { poll: Poll }) {
  const styles = useStyles();
  const vote = useVote();

  // Show the server's answer the moment the vote call returns, without waiting
  // for the summary refetch.
  const fresh = vote.variables?.pollId === poll.id ? vote.data : undefined;
  const myVote: PollChoice | null = fresh?.myVote ?? poll.myVote;
  const results = fresh?.results ?? (myVote ? poll.results : null);
  const closes = new Date(poll.closesAt).toLocaleDateString('en-US', { weekday: 'short' });

  const option = (choice: PollChoice, label: string) => {
    const share = results ? pct(choice === 'a' ? results.a : results.b, results.total) : null;
    return (
      <button
        type="button"
        role="radio"
        aria-checked={myVote === choice}
        className={mergeClasses(styles.option, myVote === choice && styles.chosen)}
        disabled={vote.isPending}
        onClick={() => myVote !== choice && vote.mutate({ pollId: poll.id, choice })}
      >
        {share !== null ? `${share}% ${label}` : label}
      </button>
    );
  };

  return (
    <section className={styles.card} aria-label="This or That">
      <div className={styles.head}>
        <span className={styles.disc} aria-hidden>
          <ArrowSwapRegular />
        </span>
        <span className={styles.label}>This or That</span>
        {poll.category && <span className={styles.category}>{poll.category}</span>}
        <Link
          to="/central/this-or-that/results"
          className={mergeClasses(styles.pie, !poll.category && styles.pieAlone)}
          aria-label="This or That, by the numbers"
          title="This or That, by the numbers"
        >
          <DataPieRegular aria-hidden />
        </Link>
      </div>
      <span className={styles.question}>{pollQuestion(poll)}</span>
      <div className={styles.options} role="radiogroup" aria-label={pollQuestion(poll)}>
        {option('a', poll.optionA)}
        {option('b', poll.optionB)}
      </div>
      <span className={styles.meta}>
        {results
          ? `${results.total} vote${results.total === 1 ? '' : 's'} · closes ${closes} · you can change your pick`
          : `Pick one to see how everyone voted · closes ${closes}`}
      </span>
      {vote.error && <span className={styles.error}>{vote.error.message}</span>}
    </section>
  );
}
